import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";

import { apiAxios } from "@/api/config";
import { useNotification } from "@/providers/NotificationProvider";
import { useAnalytics } from "@/providers/AnalyticsProvider";
import type { AuthUser } from "@/stores/auth";
import { useAuthStore } from "@/stores/auth";
import { useShallow } from "zustand/shallow";
import { mutedText } from "@/styles/variants";
import { ui, notifications } from "@/strings";
import PiMultipleCrossCancelDefaultStroke from "@markeroo/icons/src/stroke/maths/PiMultipleCrossCancelDefaultStroke";
import { Spinner } from "@/components/Spinner";

interface AuthResponse {
  user: AuthUser;
  access_token: string;
}

const logoUrl = new URL("../../images/logo-full.png", import.meta.url).href;

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { show } = useNotification();
  const analytics = useAnalytics();
  const { setUser, hydrated, user } = useAuthStore(useShallow((state) => ({
    setUser: state.setUser,
    hydrated: state.hydrated,
    user: state.user,
  })));

  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get("token");

  // Capture redirect path once on mount and track if we've already redirected
  const postAuthRedirect = useRef<string | null>(null);
  const hasRedirected = useRef(false);

  // Read and clear localStorage only once on mount
  useEffect(() => {
    const storedPath = localStorage.getItem("postAuthRedirect");
    if (storedPath) {
      postAuthRedirect.current = storedPath;
      localStorage.removeItem("postAuthRedirect");
    }
  }, []);

  useEffect(() => {
    if (hasRedirected.current) {
      return;
    }

    if (hydrated && user) {
      hasRedirected.current = true;
      void navigate(postAuthRedirect.current || "/", { replace: true });
      return;
    }

    if (!token) {
      setError(ui.labels.verificationLinkExpired);
      setIsVerifying(false);
      return;
    }

    apiAxios
      .post<AuthResponse>("/auth/verify-email", { token })
      .then((response) => {
        const { user: verifiedUser } = response.data;
        analytics.track("email_verified");
        setUser(verifiedUser);
        show(notifications.auth.emailVerified, "success");
        hasRedirected.current = true;
        void navigate(postAuthRedirect.current || "/", { replace: true });
      })
      .catch((err: unknown) => {
        analytics.track("email_verification_failed");
        const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail;
        setError(detail || notifications.auth.emailVerificationFailed);
        setIsVerifying(false);
      });
  }, [token, navigate, setUser, show, analytics, hydrated, user]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 py-10">
      <div className="flex w-full max-w-md flex-col items-center gap-6 rounded-2xl bg-white p-8 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
        <img src={logoUrl} alt="Markeroo logo" className="h-20 w-auto" />

        {isVerifying ? (
          <>
            <div className="flex items-center gap-3">
              <Spinner size="md" className="text-primary" />
              <h1 className="text-xl font-semibold text-text-theme-primary">
                {ui.actions.verifyingEmail}
              </h1>
            </div>
            <p className={mutedText}>{ui.loading.default}</p>
          </>
        ) : error ? (
          <>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-semantic-error-bg">
              <PiMultipleCrossCancelDefaultStroke className="h-6 w-6 text-semantic-error" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-semibold text-text-theme-primary">{ui.labels.verifyYourEmail}</h1>
            <p className="text-center text-sm text-semantic-error">{error}</p>
            <Link
              to="/login"
              className="mt-2 font-medium text-primary hover:text-primary-hover"
            >
              {ui.actions.backToLogin}
            </Link>
          </>
        ) : null}
      </div>
    </div>
  );
}
